import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import AssignOrderModal from "./AssignOrderModal";

const CustomerTable = ({
  customers,
  onEdit,
  onDelete,
  onView,
  searchTerm,
  onSearchChange,
  sortConfig,
  onSort,
  selectedCustomers,
  onSelectCustomer,
  onSelectAll,
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [deleteProductId, setDeleteProductId] = useState(null);

  const dispatch = useDispatch();

  const [loadingEdit, setLoadingEdit] = useState(false);

  const [editingid, setEditingId] = useState(null);

  const [editingProduct, setEditingProduct] = useState(null);

  const [editValues, setEditValues] = useState({});

  const [assignOrderId, setAssignOrderId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // State للتصدير

  const [showExportModal, setShowExportModal] = useState(false);

  const [exportProductId, setExportProductId] = useState(null);

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState("");

  const openAssignOrderModal = (orderId) => {
    setAssignOrderId(orderId);
  };

  const closeAssignOrderModal = () => {
    setAssignOrderId(null);
  };

  const sortedCustomers = useMemo(() => {
    if (!sortConfig.key) return customers;

    return [...customers].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [customers, sortConfig]);

  const handleExportClick = (productId) => {
    setExportProductId(productId);

    setShowExportModal(true);
  };

  // دالة لتأكيد التصدير (كما طلبت)

  const handleExportConfirm = async () => {
    if (!exportProductId || !fromDate || !toDate) {
      toast.error(t("يرجى ملء جميع الحقول") || "يرجى ملء جميع الحقول");

      return;
    }

    try {
      // إنشاء رابط الـ API الجديد لتصدير agents

      const url = `https://alwathiq-backend.devoracompany.com/api/v1/export/product-agents?product-id=${exportProductId}&from=${fromDate}&to=${toDate}`;

      // افتح الرابط في نافذة جديدة

      window.open(url, "_blank");

      toast.success(t("تم بدء التصدير") || "تم بدء التصدير");

      setShowExportModal(false);

      setExportProductId(null);

      setFromDate("");

      setToDate("");
    } catch (error) {
      toast.error(t("حدث خطأ أثناء التصدير") || "حدث خطأ أثناء التصدير");

      console.error("Export error:", error);
    }
  };

  const filteredCustomers = useMemo(() => {
    return sortedCustomers.filter(
      (customer) =>
        `${customer.first_name} ${customer.last_name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm)
    );
  }, [sortedCustomers, searchTerm]);

  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredCustomers.slice(startIndex, startIndex + pageSize);
  }, [filteredCustomers, currentPage, pageSize]);

  const totalPages = 50;

  const handleSort = (key) => {
    onSort(key);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: "bg-[#4ea674] text-success-foreground",
        label: t("active"),
      },
      inactive: {
        color: "bg-secondary text-secondary-foreground",
        label: t("inactive"),
      },
      pending: {
        color: "bg-warning text-warning-foreground",
        label: t("pending"),
      },
      blocked: { color: "bg-error text-error-foreground", label: t("blocked") },
    };

    const config = statusConfig[status] || statusConfig.active;
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const allSelected =
    selectedCustomers.length === paginatedCustomers.length &&
    paginatedCustomers.length > 0;
  const someSelected =
    selectedCustomers.length > 0 &&
    selectedCustomers.length < paginatedCustomers.length;

  const handleAssignOrder = () => {
    // يمكن إضافة منطق إضافي هنا إذا لزم الأمر، مثل تحديث القائمة
    closeAssignOrderModal();
  };

  useEffect(() => {}, [assignOrderId]);

  return (
    <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200">
      {/* Table Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            {t("customerRecords")}
          </h3>
          {/* <div className="flex items-center space-x-3">
            <Input
              type="search"
              placeholder={t("searchCustomers")}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-64"
            />
          </div>*/}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm text-gray-700">
          <thead className="bg-green-50 text-green-900 font-semibold">
            <tr>
              {[
                { key: "name", label: t("customerName") },
                { key: "email", label: t("email") },
                { key: "phone", label: t("phone") },
                { key: "birthday", label: t("birthday") }, // حقل جديد
                { key: "role_name", label: t("roleName") }, // حقل جديد
                { key: "assignments", label: t("مسند إلى المندوب") }, // حقل جديد
                { key: "created_at", label: t("createdAt") }, // حقل جديد
                { key: "updated_at", label: t("updatedAt") }, // حقل جديد
                { key: "status", label: t("status") },
                { key: "location", label: t("الموقع") },
              ].map((column) => (
                <th
                  key={column.key}
                  className="px-4 py-3 text-left text-sm font-medium text-foreground cursor-pointer hover:bg-muted transition-micro"
                  onClick={() => handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {sortConfig.key === column.key && (
                      <Icon
                        name={
                          sortConfig.direction === "asc"
                            ? "ChevronUp"
                            : "ChevronDown"
                        }
                        size={16}
                        className="text-primary"
                      />
                    )}
                  </div>
                </th>
              ))}
              <th className="w-24 px-4 py-3 text-center text-sm font-medium text-foreground">
                {t("actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedCustomers.map((customer) => (
              <tr
                key={customer.id}
                className="hover:bg-surface transition-micro cursor-pointer"
                onClick={() => onView(customer)}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div>
                      <div className="font-medium text-foreground">
                        {customer.first_name} {customer.last_name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {customer.email}
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {customer.phone_number || customer.phone}{" "}
                  {/* استخدم phone_number إذا كان موجود */}
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {formatDate(customer.birthday)}
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {t(customer?.role_name)}
                </td>
                {customer?.assignments?.length > 0 ? (
                  <td className="px-4 py-3 text-sm text-foreground">
                    {customer?.assignments[0]?.agent?.first_name}{" "}
                    {customer?.assignments[0]?.agent?.last_name}
                  </td>
                ) : (
                  <td className="px-4 py-3 text-sm text-foreground">
                    لم يتم إسناد العميل بعد
                  </td>
                )}

                <td className="px-4 py-3 text-sm text-foreground">
                  {formatDate(customer.created_at)}
                </td>
                <td className="px-4 py-3 text-sm text-foreground">
                  {formatDate(customer.updated_at)}
                </td>

                <td className="px-4 py-3">
                  {getStatusBadge(customer.role?.name || customer.role_name)}
                </td>

                <td className="px-4 py-3">
                  <a
                    href={customer?.location_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "blue" }}
                  >
                    {customer?.location_link}
                  </a>
                </td>

                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center space-x-1">
                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onView(customer)}
                      className="w-8 h-8"
                    >
                      <Icon name="Eye" size={16} />
                    </button>

                    {customer?.role_name == "AGENT" && (
                      <button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportClick(customer.id)}
                        title={t("exportAgents")}
                      >
                        <Icon
                          name="Download"
                          size={16}
                          className="text-primary"
                        />
                      </button>
                    )}

                    {customer?.role_name != "AGENT" && (
                      <button
                        variant="ghost"
                        size="icon"
                        onClick={() => openAssignOrderModal(customer.enc_id)}
                        title={t("assignToAgent")}
                      >
                        <Icon
                          name="UserCheck"
                          size={16}
                          className="mr-2 ml-2"
                        />
                      </button>
                    )}
                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(customer)}
                      className="w-8 h-8"
                    >
                      <Icon name="Edit" size={16} />
                    </button>

                    <button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(customer)}
                      className="w-8 h-8 text-error hover:text-error"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {paginatedCustomers.map((customer) => (
          <div key={customer.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div>
                  <div className="font-medium text-foreground">
                    {customer.first_name} {customer.last_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {customer.company || "N/A"}
                  </div>
                </div>
              </div>
              {getStatusBadge(customer.role?.name || customer.role_name)}
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Mail" size={14} className="text-muted-foreground" />
                <span className="text-foreground">{customer.email}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon
                  name="Phone"
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-foreground">
                  {customer.phone_number || customer.phone}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon
                  name="Calendar"
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-muted-foreground">
                  {t("birthday")}: {formatDate(customer.birthday)}{" "}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="User" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  {t("roleName")}: {customer.role_name || customer.role?.name}{" "}
                </span>
              </div>

              <div className="flex items-center space-x-2 text-sm">
                <span className="text-muted-foreground">
                  {customer?.assignments[0]?.agent?.first_name}{" "}
                  {customer?.assignments[0]?.agent?.last_name}
                </span>
              </div>

              <div className="flex items-center space-x-2 text-sm">
                <Icon
                  name="Clock"
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-muted-foreground">
                  {t("createdAt")}: {formatDate(customer.created_at)}{" "}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon
                  name="Clock"
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-muted-foreground">
                  {t("updatedAt")}: {formatDate(customer.updated_at)}{" "}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-2 gap-2">
              <button
                variant="outline"
                size="sm"
                onClick={() => onView(customer)}
              >
                <Icon name="Eye" size={16} className="mr-2" />
                {t("view")}
              </button>

              {customer?.role_name == "AGENT" && (
                <button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExportClick(product.id)}
                  title={t("exportAgents")}
                >
                  <Icon
                    name="Download"
                    size={16}
                    className="text-primary mr-2 ml-2"
                  />
                </button>
              )}

              {customer?.role_name != "AGENT" && (
                <button
                  variant="ghost"
                  size="icon"
                  onClick={() => openAssignOrderModal(customer.enc_id)}
                  title={t("assignToAgent")}
                >
                  <Icon name="UserCheck" size={16} className="mr-2" />
                </button>
              )}
              <button
                variant="outline"
                size="sm"
                onClick={() => onEdit(customer)}
              >
                <Icon name="Edit" size={16} className="mr-2" />
                {t("edit")}
              </button>
              <button
                variant="outline"
                size="sm"
                onClick={() => onDelete(customer)}
                className="text-error hover:text-error"
              >
                <Icon name="Trash2" size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              تصدير معلومات المندوب
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                {t("من تاريخ") || "من تاريخ"}
              </label>

              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                {t("إلى تاريخ") || "إلى تاريخ"}
              </label>

              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="flex justify-end space-x-2 gap-4">
              <Button onClick={handleExportConfirm}>
                {t("تصدير") || "تصدير"}
              </Button>

              <button
                onClick={() => {
                  setShowExportModal(false);

                  setExportProductId(null);

                  setFromDate("");

                  setToDate("");
                }}
                variant="secondary"
              >
                {t("إلغاء") || "إلغاء"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Order Modal */}
      {assignOrderId && (
        <AssignOrderModal
          orderId={assignOrderId}
          onClose={closeAssignOrderModal}
          onAssignOrder={handleAssignOrder}
        />
      )}
    </div>
  );
};

export default CustomerTable;
